import { Button } from "@blurb/codex-react";
import { useForm } from "@mantine/form";
import { GraphQL, isGraphQLError } from "@rpi/openapi-api";
import { useToasts } from "@rpi/openapi-core";
import { useQueryClient } from "@tanstack/react-query";
import { RpaButtonLink } from "../../components/RpiLink";
import { RpaTextInput } from "../../components/inputFields/RpaTextInput";
import { useAuthUser } from "../../hooks/useAuthUser";
import { RpaDrawer } from "../../rpi-core/drawer/RpaDrawer";
import { createRpiDrawerView } from "../../rpi-core/drawer/RpiDrawer";

const FORM_ID = "generate-api-key-form";
// The agreed product cap for a key description. The schema declares no length constraint, so
// this isn't a storage limit and the backend won't reject a longer one.
export const MAX_KEY_DESCRIPTION_LENGTH = 512;
export const KEY_DESCRIPTION_REQUIRED_ERROR = "Please enter a description";
export const KEY_DESCRIPTION_TOO_LONG_ERROR = `Please enter a description with ${MAX_KEY_DESCRIPTION_LENGTH} or fewer characters`;

export const ApiCredentialCreateView = createRpiDrawerView(GenerateKeyDrawer);

interface GenerateKeyFormValues {
  description: string;
}

export function keyDescriptionError(description: string): string | null {
  const trimmed = description.trim();
  if (trimmed.length === 0) return KEY_DESCRIPTION_REQUIRED_ERROR;
  if (trimmed.length > MAX_KEY_DESCRIPTION_LENGTH) return KEY_DESCRIPTION_TOO_LONG_ERROR;
  return null;
}

export function keyGeneratedMessage(apiKey: string): string {
  return `New API key '${apiKey}' was generated successfully!`;
}

interface GenerateKeyDrawerProps {
  opened: boolean;
  onClose: () => void;
}

function GenerateKeyDrawer({ opened, onClose }: GenerateKeyDrawerProps) {
  const user = useAuthUser();
  const queryClient = useQueryClient();
  const { showSuccessToast, showErrorToast } = useToasts();

  const { mutate: createApiCredential, isLoading } = GraphQL.useCreateApiCredentialMutation({
    onSuccess: (data) => {
      showSuccessToast(keyGeneratedMessage(data.apiCredentialsCreate.apiKey));
      onClose();
      // Not awaited: React Query keeps the mutation loading until onSuccess settles, which would
      // hold every close affordance disabled for the length of a background refetch.
      queryClient.invalidateQueries({ queryKey: ["ApiCredentials"] }).catch(() => undefined);
    },
    onError: (error) => {
      showErrorToast(isGraphQLError(error) ? error.message : "Failed to generate API key");
    },
  });

  const form = useForm<GenerateKeyFormValues>({
    initialValues: { description: "" },
    validate: { description: keyDescriptionError },
  });

  const handleSubmit = ({ description }: GenerateKeyFormValues) => {
    // Generating a key is irreversible, so never let a second submit through — the footer
    // button is portaled out of the form and reaches it via form=, so its disabled state
    // isn't the only thing standing between Enter and a duplicate credential.
    if (isLoading) return;
    createApiCredential({ input: { description: description.trim(), isSandbox: user.isSandbox } });
  };

  const closeUnlessGenerating = () => {
    if (isLoading) return;
    onClose();
  };

  return (
    <RpaDrawer title="Generate New Key" opened={opened} closeDisabled={isLoading} onClose={closeUnlessGenerating}>
      <form id={FORM_ID} onSubmit={form.onSubmit(handleSubmit)}>
        <RpaDrawer.Body>
          <RpaTextInput label="Key Description" {...form.getInputProps("description")} />
        </RpaDrawer.Body>

        <RpaDrawer.Footer>
          <RpaDrawer.FooterActions>
            <Button
              form={FORM_ID}
              type="submit"
              variant="filled"
              intent="primary"
              size="large"
              fullWidth
              loading={isLoading}
            >
              Generate
            </Button>
            <RpaButtonLink data-testid="cancel-generate-key" disabled={isLoading} onClick={closeUnlessGenerating}>
              Cancel
            </RpaButtonLink>
          </RpaDrawer.FooterActions>
        </RpaDrawer.Footer>
      </form>
    </RpaDrawer>
  );
}
